import type { Grabber } from '../input/Grabbers';
import type { RoomHub } from '../net/roomHub';
import { bytesToBase64 } from '../net/base64';
import type { LabelCaptureResult, NodeDto } from '../net/wire';
import { MicrophoneCapture } from './MicrophoneCapture';

/** Shown on the node's own label while this connection's microphone is actively capturing for
 *  it (button held) — see `NodeLabelController`'s own top-level doc for why the node itself,
 *  not a wall panel, is where this belongs. */
const LISTENING_STATUS_TEXT = 'listening…';

/** Shown once the button is released and the round trip to Gemini is under way — the exact
 *  window the headset report's "refused... without any feedback" fell into, since the client
 *  used to forget which node it was even for the instant the button came up. */
const PROCESSING_STATUS_TEXT = 'transcribing…';

/** Shown on a failed transcription — the owner's own suggested wording. */
const FAILED_STATUS_TEXT = "didn't catch that";

/** How long a processing round trip is given before this connection gives up on it locally and
 *  frees itself to try again (`armProcessingTimeout`) — generous against a real transcription
 *  (a few spoken words, a network round trip to Gemini, ordinarily a few seconds), short enough
 *  that a genuinely lost broadcast recovers within the same session rather than silently
 *  refusing every later press for the rest of it. */
const PROCESSING_TIMEOUT_MS = 20_000;

/** How long the 'failed' status stays on the node's own label before yielding back to its real
 *  text, absent a fresh attempt clearing it sooner (`armFailureDisplay`). */
const FAILED_STATUS_DISPLAY_MS = 4_000;

/**
 * Lets someone name a shape by speaking, while holding it — plan/open-items.md: "Hold the
 * shape, hold a dominant-hand face button, speak, release." The held button is the whole
 * point, not incidental: the room's microphone is on by default and streams to everyone
 * already (`AssistantController`'s own `capturing`), so a dwell or a toggle would be
 * ambiguous about who a spoken word was for. A held button is unambiguous — "this is for
 * the system" — the same grammar `talkButton` already uses for an assistant turn, just read
 * off the *dominant* hand instead of the off hand, and gated on a node actually being held
 * rather than always-on.
 *
 * Logically a new, separate capture from `AssistantController`'s own microphone use — labelling
 * never routes through a conversational turn (`AssistantPipeline`), so there is nothing to
 * share with it beyond audio itself. It is *not*, however, a separate device: both this class
 * and `AssistantController` subscribe to the one shared `MicrophoneCapture` instance
 * `main.ts` constructs once and hands to both (`MicrophoneCapture`'s own module doc, "one hot
 * recorder, N consumers") — opening a second, independent microphone stream for the identical
 * hardware would be wasteful even where it works, and was the mechanism behind the bug the next
 * paragraph describes.
 *
 * **A two-person headset session (2026-08-12) reported this as "inconsistent at best... got
 * it working once and then it refused to work the second and third time without any
 * feedback."** Three separate, confirmed causes, all addressed here:
 *
 * - *Why it sent no audio at all.* The room's microphone is on by default
 *   (`AssistantController`'s Y), so a recorder was normally already running by the time anyone
 *   reached for this feature. `MicrophoneCapture.start()` used to be a single-`onChunk`
 *   singleton that silently no-opped for every caller after the first — this class's own
 *   `capture.start(...)` call below returned immediately with its callback never registered,
 *   so nothing this class ever streamed reached the server. The "got it working once" in the
 *   report was almost certainly the one case where the mic happened to be off, the only state
 *   in which `start()` actually did anything. `MicrophoneCapture`'s fan-out — every subscriber
 *   gets every chunk, not just the first one registered — is the fix; see its own module doc.
 * - *Why it refused.* `LabelCaptureRegistry.TryOpen` (server) holds this connection's capture
 *   open from the button's release all the way through the transcription round trip to
 *   Gemini — a real network call, seconds long — and refuses any second press landing in that
 *   window, for *any* node, not just the one already in flight (its own doc comment). The
 *   client used to forget which node it was even for the instant the button came up
 *   (`capturedNodeId` was nulled in the same breath as `endLabelCapture()` was sent), so it
 *   had nothing to show and no way to refuse locally — every retry round-tripped to the
 *   server only to be told what the client could have known on its own. `processingNodeId`
 *   below is that missing knowledge, and `begin()`'s own guard on it is what stops a doomed
 *   second request from ever being sent.
 * - *Why it was silent.* Routed through `showNotice`, a `position: fixed` 2D element invisible
 *   inside an XR session — fixed separately, in PR #136 (the wall-mounted admin error log),
 *   which `onNotice` still reaches unchanged. What was still missing even after that fix: a
 *   person holding a shape and speaking its name is looking at *that shape*, not at a wall
 *   across the room. `onLabelStatusChanged` puts a transient status — listening, then
 *   transcribing, then (on failure) "didn't catch that" — directly on the node's own label,
 *   where the attention already is. `RoomView.setLabelStatus` is the current renderer.
 */
export class NodeLabelController {
  private capturing = false;

  /**
   * The node held when recording *started* — captured once, here, and used for every
   * subsequent step of this capture. Never re-read from possession state later: a grab can
   * end mid-recording (the room's own transcription round trip takes seconds), and reading
   * "what is held now" at that point would silently mislabel whatever is in hand next, or
   * silently do nothing at all — the exact class of bug CLAUDE.md's "A callback's own
   * argument is the value that was true when it fired" documents shipping once already, for
   * a typed label (`setNodeText`'s own history).
   */
  private capturedNodeId: string | null = null;

  /**
   * Set the instant the button is released, cleared only by whichever terminal signal for
   * *that exact node* arrives first: a successful `NodeUpdated` broadcast
   * (`handleNodeUpdated`), a matching `LabelCaptureFailed` (`handleFailed`), or
   * `PROCESSING_TIMEOUT_MS` elapsing with neither (`armProcessingTimeout`). Distinct from
   * `capturedNodeId`, which only lives while audio is actively streaming (`capturing`): this
   * field covers the *next* phase — the round trip to Gemini the server-side registry still
   * holds this connection's capture open for. `begin()` reads it to refuse a second press
   * locally rather than asking the server a question this connection already knows the answer
   * to — see this class's own top-level doc for the headset report this exists to fix.
   */
  private processingNodeId: string | null = null;

  /**
   * Backstop for a lost `NodeUpdated`/`LabelCaptureFailed` — CLAUDE.md's own standing lesson
   * ("`onEnded` must not be the only way out of a playback state") applied here. Without this,
   * a dropped broadcast would leave `processingNodeId` set for the rest of the connection's
   * life, silently refusing every later press with no way to recover short of a reload. Armed
   * in `end()`, disarmed by whichever terminal signal actually arrives first.
   */
  private processingTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  /**
   * Auto-clears the 'failed' status text on the node's own label a while after a failure, so a
   * node that already had a perfectly good label does not keep showing "didn't catch that"
   * forever just because nobody tried again — `ApplyLabelAsync` leaves an existing label
   * completely untouched on failure (`LabelTranscriptionPipeline.cs`), so nothing about the
   * real text ever changed; only this transient overlay needs to go away on its own.
   */
  private failureClearHandle: ReturnType<typeof setTimeout> | null = null;

  /**
   * The one stable function reference this capture's subscription uses for both
   * `capture.start()` and `capture.stop()` while it is open — `MicrophoneCapture` identifies a
   * subscriber by that reference (see its own module doc), so a fresh arrow function per call
   * would never successfully unsubscribe. `null` whenever no capture is open; set in `begin()`
   * right alongside `capturing = true`, cleared wherever that flips back to `false`
   * (`end()`, the failed-start catch below, `releaseImmediately()`).
   */
  private chunkListener: ((bytes: Uint8Array) => void) | null = null;

  private readonly mimeType: string;

  /** Surfaced to the user; the caller decides how (`main.ts` wires this to `showNotice`, the
   *  same transient toast every other refusal and confirmation in this app already uses). */
  onNotice: ((message: string) => void) | null = null;

  /**
   * Fired whenever this connection's own label-capture status for a node should be shown (or
   * cleared) directly on that node's own label, rather than only a wall-mounted panel or an
   * easy-to-miss 2D toast — see this class's own top-level doc for why. `text: null` means
   * "show the node's real label again; nothing is in flight for it." This class owns only the
   * wording and when it changes; the caller (`main.ts` -> `RoomView.setLabelStatus`) owns
   * rendering it.
   */
  onLabelStatusChanged: ((nodeId: string, text: string | null) => void) | null = null;

  constructor(
    private readonly hub: RoomHub,
    private readonly capture: MicrophoneCapture = new MicrophoneCapture(),
  ) {
    this.mimeType = MicrophoneCapture.pickMimeType();

    // Sent only to this connection (RoomSyncHub.LabelCaptureFailed's own remarks) — nobody
    // else in the room ever knew a capture was open, so nobody else needs to hear it failed.
    // A success carries no matching event: the label lands as an ordinary NodeUpdated,
    // handled the same as any other edit, in main.ts — which is also why a success is *not*
    // also registered here as `hub.on('nodeUpdated', ...)`. `RoomHub.on` keeps exactly one
    // handler per event name (`this.handlers[event] = handler` overwrites, not adds), and
    // main.ts already owns that registration for its own room-state wiring; a second call here
    // would silently clobber it. `handleNodeUpdated` below is a plain public method instead,
    // called from inside main.ts's own single `nodeUpdated` handler alongside `state.upsertNode`
    // — the same fan-out-from-one-registration shape `voiceStreamStarted` already uses there.
    hub.on('labelCaptureFailed', (nodeId, reason) => this.handleFailed(nodeId, reason));
  }

  /** True while this connection's own capture is open — for a caller that wants to render a
   *  "listening" affordance beyond the plain text notice this class already posts. */
  get isCapturing(): boolean {
    return this.capturing;
  }

  /** Which node this capture is for, while one is open — null otherwise. */
  get capturingNodeId(): string | null {
    return this.capturedNodeId;
  }

  /** True while this connection is waiting on a transcription round trip for a node whose
   *  button has already been released — see `processingNodeId`'s own doc comment. `processingNodeId`
   *  itself stays private: `begin()`'s own guard is the one thing that reads *which* node, and
   *  every outside caller only needs this boolean. */
  get isProcessing(): boolean {
    return this.processingNodeId !== null;
  }

  /**
   * Read once per frame from the *dominant* grabber's primary face button — `talkButton`
   * reused rather than duplicated: `input/Grabbers.ts` resolves it identically for either
   * hand (the same physical component, `x-button`/`a-button`), and `AssistantController`
   * only ever reads it off the *off* hand, so the dominant hand's own copy of the same field
   * has been sitting unused since M-something. `micToggleButton` (the dominant hand's other
   * face button) is deliberately left unbound — the owner wants it for a later,
   * context-specific action.
   *
   * Silently does nothing on a press while no node is held: the gesture is "hold the shape,
   * *then* hold the button," not a standalone push-to-talk.
   */
  tick(dominant: Grabber | undefined, heldNodeId: string | null): void {
    const button = dominant?.talkButton;

    if (!button) {
      return;
    }

    if (button.justPressed) {
      void this.begin(heldNodeId);
    }

    if (button.justReleased) {
      void this.end();
    }
  }

  /** Drops anything held, so a page refresh does not leave a capture open server-side. */
  releaseImmediately(): void {
    // Whichever of the two phases this connection was in — still actively capturing, or
    // already released and waiting on the round trip — its status overlay (if any) must not
    // survive a reload it will never see the other end of. At most one of these is ever
    // non-null at once (see each field's own doc comment), so this is not a real ambiguity.
    const staleNodeId = this.capturedNodeId ?? this.processingNodeId;

    if (this.chunkListener !== null) {
      this.capture.stop(this.chunkListener);
      this.chunkListener = null;
    }

    this.capturing = false;
    this.capturedNodeId = null;
    this.processingNodeId = null;
    this.disarmProcessingTimeout();
    this.clearFailureDisplay();

    if (staleNodeId !== null) {
      this.onLabelStatusChanged?.(staleNodeId, null);
    }
  }

  private async begin(heldNodeId: string | null): Promise<void> {
    if (heldNodeId === null || this.capturing) {
      return;
    }

    if (this.processingNodeId !== null) {
      // The server-side registry still holds this connection's previous capture open until
      // the pipeline's own `finally` releases it (LabelCaptureRegistry.TryOpen's own doc
      // comment) — a press landing in this window would be refused server-side with
      // "already-open" regardless of which node it targets. Handled locally instead of
      // round-tripping to be told the same thing this connection already knows — see this
      // class's own top-level doc for the headset report this exists to fix.
      this.onNotice?.('still working on the last label — try again in a moment');
      return;
    }

    let result: LabelCaptureResult;

    try {
      result = await this.hub.beginLabelCapture(heldNodeId, this.mimeType);
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
      return;
    }

    if (!result.granted) {
      this.onNotice?.(
        result.reason === 'already-open'
          ? 'already listening for a label'
          : 'no assistant configured — cannot label by voice',
      );
      return;
    }

    // Captured here, the moment recording actually starts — see this field's own doc
    // comment for why this must not be re-read later.
    this.capturedNodeId = heldNodeId;
    this.capturing = true;
    this.onNotice?.('listening for a label…');

    // A fresh attempt beats a stale one — cancel any still-pending auto-clear from an earlier
    // failure on this same node so it cannot fire mid-way through this new attempt and wipe
    // whatever status this one is about to show.
    this.clearFailureDisplay();
    this.onLabelStatusChanged?.(heldNodeId, LISTENING_STATUS_TEXT);

    // Stored, not an inline lambda passed straight to `start()`: `MicrophoneCapture` tells one
    // subscriber's `stop()` apart from another's by this exact reference (its own module doc),
    // so `end()` below needs the identical function it registered here, not a fresh one.
    const listener = (bytes: Uint8Array): void => {
      this.hub.streamLabelAudioChunk({ data: bytesToBase64(bytes) });
    };
    this.chunkListener = listener;

    try {
      await this.capture.start(this.mimeType, listener);
    } catch (error) {
      console.warn('Label capture: failed to start microphone capture.', error);
      this.onNotice?.('microphone unavailable');

      // The server still thinks a capture is open — end it honestly rather than leaving a
      // channel held with nothing ever going to arrive on it.
      this.capturing = false;
      this.capturedNodeId = null;
      this.chunkListener = null;

      // Nothing was ever actually captured, so there is nothing to call "not caught" —
      // straight back to the node's real label, not the 'failed' wording.
      this.onLabelStatusChanged?.(heldNodeId, null);
      void this.hub.endLabelCapture().catch(() => {});
    }
  }

  /**
   * Releasing the button always ends capture, regardless of whether the node it was for is
   * still held — the two are independent physical controls (grip vs. face button), and
   * letting go of the shape mid-sentence must not cut the recording off; only the button
   * release does that.
   */
  private async end(): Promise<void> {
    if (!this.capturing) {
      return;
    }

    this.capturing = false;

    // Never null here: `capturing` only ever goes true right alongside `chunkListener` being
    // set (`begin()`), and the one place that flips it back to false without a matching
    // `capture.stop()` call (the failed-start catch in `begin()`) already clears
    // `chunkListener` itself, so `capturing` and `chunkListener !== null` move together.
    if (this.chunkListener !== null) {
      this.capture.stop(this.chunkListener);
      this.chunkListener = null;
    }

    const nodeId = this.capturedNodeId;
    this.capturedNodeId = null;

    if (nodeId !== null) {
      // The registry still holds this connection's capture open for the round trip to Gemini
      // (LabelCaptureRegistry.TryOpen's own doc comment) — from here until a terminal signal
      // arrives, a second press would be refused server-side. `begin()`'s own guard on
      // `processingNodeId` handles that locally instead; this is what arms it.
      this.processingNodeId = nodeId;
      this.onLabelStatusChanged?.(nodeId, PROCESSING_STATUS_TEXT);
      this.armProcessingTimeout(nodeId);
    }

    try {
      await this.hub.endLabelCapture();
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * Called by `main.ts` from inside its own single `nodeUpdated` handler, alongside
   * `state.upsertNode` — not registered as `hub.on('nodeUpdated', ...)` here directly; see the
   * constructor's own remarks on why that would clobber main.ts's registration for the same
   * event. A successful transcription reaches this connection exactly this way:
   * `LabelTranscriptionPipeline.RunAsync` broadcasts `NodeUpdated` to the whole Group on
   * success, speaker included (there is no separate "label succeeded" event to listen for
   * instead) — so this is genuinely the only signal a success ever produces.
   */
  handleNodeUpdated(node: NodeDto): void {
    if (node.id !== this.processingNodeId) {
      return;
    }

    this.disarmProcessingTimeout();
    this.processingNodeId = null;
    this.onLabelStatusChanged?.(node.id, null);
  }

  private handleFailed(nodeId: string, reason: string): void {
    this.onNotice?.(
      reason === 'empty'
        ? "didn't catch that — try again"
        : reason === 'lock-lost'
          ? "label not saved — you'd already let go"
          : reason === 'not-found'
            ? 'label not saved — that shape is gone'
            : 'label not saved',
    );

    // Only this connection's own currently-outstanding capture is worth touching here — a
    // stale/late failure for a node this connection has already moved on from (the processing
    // timeout backstop already gave up on it, or a fresh capture started for something else)
    // must not clobber whatever status is showing for that newer, unrelated attempt. The same
    // "isStillCurrent" shape `AssistantController.playClip` already uses for a stray belated
    // browser event, applied here to a stray belated hub one.
    if (nodeId !== this.processingNodeId) {
      return;
    }

    this.disarmProcessingTimeout();
    this.processingNodeId = null;
    this.onLabelStatusChanged?.(nodeId, FAILED_STATUS_TEXT);
    this.armFailureDisplay(nodeId);
  }

  /** Arms the processing backstop — see `processingTimeoutHandle`'s own doc comment for why it
   *  exists at all. Always disarms first, so a caller never has to worry about a stale timer
   *  from an earlier capture outliving this one. */
  private armProcessingTimeout(nodeId: string): void {
    this.disarmProcessingTimeout();
    this.processingTimeoutHandle = setTimeout(() => {
      this.processingTimeoutHandle = null;

      if (this.processingNodeId !== nodeId) {
        return;
      }

      this.processingNodeId = null;
      this.onNotice?.("label capture timed out — didn't hear back in time");
      this.onLabelStatusChanged?.(nodeId, null);
    }, PROCESSING_TIMEOUT_MS);
  }

  private disarmProcessingTimeout(): void {
    if (this.processingTimeoutHandle !== null) {
      clearTimeout(this.processingTimeoutHandle);
      this.processingTimeoutHandle = null;
    }
  }

  /** Arms the 'failed' status's own auto-clear — see `failureClearHandle`'s own doc comment. */
  private armFailureDisplay(nodeId: string): void {
    this.clearFailureDisplay();
    this.failureClearHandle = setTimeout(() => {
      this.failureClearHandle = null;
      this.onLabelStatusChanged?.(nodeId, null);
    }, FAILED_STATUS_DISPLAY_MS);
  }

  private clearFailureDisplay(): void {
    if (this.failureClearHandle !== null) {
      clearTimeout(this.failureClearHandle);
      this.failureClearHandle = null;
    }
  }
}
